import { type MutationOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useFlaskApi } from '~/auth/flask-api';
import { type Cart, LenientCartSchema, emptyCart } from '~/types/cart';
import type { QueryOpts } from '~/types/queryOptions';

import { useRootLoader } from './useUser';

const GET_CART_QUERY_KEY = ['get-cart-api'] as const;

export function useGetCart(options: QueryOpts<Cart> = {}) {
  const api = useFlaskApi();
  const { user, isClinician, isMarketplaceUser } = useRootLoader();

  return useQuery<Cart, Error>({
    queryKey: GET_CART_QUERY_KEY,
    queryFn: async () => {
      const response = await api.get('/v2/products/cart');
      const parsed = LenientCartSchema.safeParse(response.data.cart);
      return parsed.success ? parsed.data : emptyCart;
    },
    enabled: !!user && (isClinician || isMarketplaceUser),
    ...options,
  });
}

type CartUpdateOptions = {
  cart?: Cart;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
};

export function useUpdateCart({ cart: staticCart, onSuccess, onError }: CartUpdateOptions = {}) {
  const api = useFlaskApi();
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, Cart | void>({
    mutationFn: async (cart) => api.post('/v2/products/cart', { cart: cart || staticCart }),
    onSuccess: (_data, cart) => {
      const nextCart = cart || staticCart;
      if (nextCart) {
        queryClient.setQueryData(GET_CART_QUERY_KEY, nextCart);
      }
      queryClient.invalidateQueries({ queryKey: GET_CART_QUERY_KEY });
      onSuccess?.();
    },
    onError,
  });
}

export function useDeleteCart(options: MutationOptions = {}) {
  const api = useFlaskApi();
  const queryClient = useQueryClient();

  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: async () => api.delete('/v2/products/cart'),
    onSuccess: (...args) => {
      queryClient.setQueryData(GET_CART_QUERY_KEY, emptyCart);
      queryClient.invalidateQueries({ queryKey: GET_CART_QUERY_KEY });
      onSuccess?.(...args);
    },
    ...restOptions,
  });
}
